/**
 * Stanlee India — Product Explorer
 * Vanilla JS, isolated, no dependencies.
 *
 * All facet values (sizes, display types, operating systems, brand
 * categories, price range) are read live from the data-* attributes that
 * Liquid renders on each product card. Nothing here is hardcoded — add a
 * product with a new screen size or tag it with a new Display/OS value and
 * it will appear as a filter option automatically on next page load.
 *
 * NOTE ON DEDUPLICATION:
 * The Liquid template renders one card per (product x collection loop).
 * A product that belongs to both the "All" source collection and one or
 * more category-tab collections will therefore produce multiple raw card
 * elements for the very same product. Those raw cards are merged here
 * into a single product record keyed by data-handle (falls back to title
 * if data-handle is absent), while preserving every tab it belongs to, so
 * category filtering keeps working correctly after the merge.
 */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-pe-root]');

  roots.forEach(function (root) {
    initExplorer(root);
  });

  function initExplorer(root) {
    var grid = root.querySelector('[data-pe-grid]');
    var cardEls = Array.prototype.slice.call(root.querySelectorAll('[data-pe-card]'));
    var perPage = parseInt(root.getAttribute('data-per-page'), 10) || 12;

    if (!cardEls.length) return;

    var rawCards = cardEls.map(function (el, index) {
      return {
        el: el,
        handle: (el.getAttribute('data-handle') || '').trim().toLowerCase(),
        title: (el.getAttribute('data-title') || '').toLowerCase(),
        tab: (el.getAttribute('data-tab') || '').trim(),
        price: parseFloat(el.getAttribute('data-price')) || 0,
        sizes: splitPipeOrComma(el.getAttribute('data-sizes')),
        display: splitPipeOrComma(el.getAttribute('data-display')),
        os: splitPipeOrComma(el.getAttribute('data-os')),
        type: (el.getAttribute('data-type') || '').trim(),
        available: el.getAttribute('data-available') === 'true',
        created: parseInt(el.getAttribute('data-created'), 10) || 0,
        rank: parseInt(el.getAttribute('data-rank'), 10) || index
      };
    });

    // Detach cards from DOM; grid is rebuilt on every render from `products`.
    rawCards.forEach(function (p) { p.el.remove(); });

    // Merge duplicate cards (same product rendered once per collection
    // loop) into single product records, keeping every tab it belongs to.
    var products = dedupeByHandle(rawCards);

    var state = {
      category: 'all',
      search: '',
      sort: 'newest',
      sizes: new Set(),
      display: new Set(),
      os: new Set(),
      availability: new Set(),
      type: new Set(),
      priceMin: null,
      priceMax: null,
      page: 1
    };

    var priceBounds = computePriceBounds(products);
    state.priceMin = priceBounds.min;
    state.priceMax = priceBounds.max;

    buildFacet(root, 'size', uniqueValues(products, 'sizes'), state.sizes, applyAndRender);
    buildFacet(root, 'display', uniqueValues(products, 'display'), state.display, applyAndRender);
    buildFacet(root, 'os', uniqueValues(products, 'os'), state.os, applyAndRender);
    buildFacet(root, 'type', uniqueValues(products, 'type'), state.type, applyAndRender);
    buildAvailabilityFacet(root, state.availability, applyAndRender);
    hideEmptyFilterGroups(root);
    initPriceRange(root, priceBounds, state, applyAndRender);
    initCategoryBar(root, products, state, applyAndRender);
    initSearch(root, state, applyAndRender);
    initSort(root, state, applyAndRender);
    initMobileDrawer(root);
    initClearAll(root, state, applyAndRender);

    applyAndRender();

    function applyAndRender() {
      var filtered = filterProducts(products, state);
      filtered = sortProducts(filtered, state.sort);
      renderGrid(root, grid, filtered, state, perPage);
      renderResultCount(root, filtered.length, products.length);
      renderPagination(root, filtered, state, perPage, applyAndRender);
      updateFilterCountBadge(root, state);
    }
  }

  /* ---------------- helpers: data parsing ---------------- */

  function splitPipeOrComma(str) {
    if (!str) return [];
    return str
      .split(/[|,]/)
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  /**
   * Collapse raw per-collection-loop cards into one record per unique
   * product. Identity is data-handle (Shopify product handle) when
   * present; falls back to normalized title so the page still works if
   * data-handle hasn't been added to the card snippet yet. Every tab the
   * duplicate cards belonged to is preserved in `tabs`, so a product that
   * legitimately belongs to "all" + "android-tv" still matches both.
   */
  function dedupeByHandle(rawCards) {
    var byKey = {};
    var order = [];

    rawCards.forEach(function (p) {
      var key = p.handle || p.title;

      if (!byKey[key]) {
        p.tabs = {};
        p.tabs[p.tab] = true;
        byKey[key] = p;
        order.push(key);
      } else {
        var existing = byKey[key];
        existing.tabs[p.tab] = true;
        // Prefer the lowest rank / most complete facet data across the
        // duplicate cards so sort order and filters stay accurate
        // regardless of which loop happened to produce the "kept" card.
        if (p.rank < existing.rank) existing.rank = p.rank;
        if (p.created > existing.created) existing.created = p.created;
        existing.sizes = mergeUnique(existing.sizes, p.sizes);
        existing.display = mergeUnique(existing.display, p.display);
        existing.os = mergeUnique(existing.os, p.os);
      }
    });

    return order.map(function (key) { return byKey[key]; });
  }

  function mergeUnique(a, b) {
    var set = new Set(a.concat(b));
    return Array.from(set);
  }

  function uniqueValues(products, key) {
    var set = new Set();
    products.forEach(function (p) {
      var val = p[key];
      if (Array.isArray(val)) {
        val.forEach(function (v) { if (v) set.add(v); });
      } else if (val) {
        set.add(val);
      }
    });
    return Array.from(set).sort(naturalSort);
  }

  function naturalSort(a, b) {
    var na = parseFloat(a);
    var nb = parseFloat(b);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return a.localeCompare(b);
  }

  function computePriceBounds(products) {
    var prices = products.map(function (p) { return p.price; }).filter(function (p) { return !isNaN(p); });
    if (!prices.length) return { min: 0, max: 100000 };
    return {
      min: Math.floor(Math.min.apply(null, prices)),
      max: Math.ceil(Math.max.apply(null, prices))
    };
  }

  /* ---------------- facets: chips ---------------- */

  function buildFacet(root, key, values, stateSet, onChange) {
    var container = root.querySelector('[data-pe-facet="' + key + '"]');
    if (!container) return;

    if (!values.length) {
      var group = container.closest('.pe-filter-group');
      if (group) group.hidden = true;
      return;
    }

    values.forEach(function (value) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'pe-chip';
      chip.textContent = value;
      chip.setAttribute('data-value', value);
      chip.addEventListener('click', function () {
        if (stateSet.has(value)) {
          stateSet.delete(value);
          chip.classList.remove('is-active');
        } else {
          stateSet.add(value);
          chip.classList.add('is-active');
        }
        onChange();
      });
      container.appendChild(chip);
    });
  }

  function buildAvailabilityFacet(root, stateSet, onChange) {
    var container = root.querySelector('[data-pe-facet="availability"]');
    if (!container) return;
    var options = ['In Stock', 'Out of Stock'];
    options.forEach(function (label) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'pe-chip';
      chip.textContent = label;
      chip.addEventListener('click', function () {
        if (stateSet.has(label)) {
          stateSet.delete(label);
          chip.classList.remove('is-active');
        } else {
          stateSet.add(label);
          chip.classList.add('is-active');
        }
        onChange();
      });
      container.appendChild(chip);
    });
  }

  function hideEmptyFilterGroups(root) {
    root.querySelectorAll('[data-pe-filter-block]').forEach(function (group) {
      var body = group.querySelector('.pe-filter-group__body');
      if (body && !body.children.length) group.hidden = true;
    });
  }

  /* ---------------- price range slider ---------------- */

  function initPriceRange(root, bounds, state, onChange) {
    var minInput = root.querySelector('[data-pe-price-min-input]');
    var maxInput = root.querySelector('[data-pe-price-max-input]');
    var fill = root.querySelector('[data-pe-range-fill]');
    var minLabel = root.querySelector('[data-pe-price-min-label]');
    var maxLabel = root.querySelector('[data-pe-price-max-label]');

    if (!minInput || !maxInput) return;

    [minInput, maxInput].forEach(function (input) {
      input.min = bounds.min;
      input.max = bounds.max;
      input.step = Math.max(1, Math.round((bounds.max - bounds.min) / 100));
    });
    minInput.value = bounds.min;
    maxInput.value = bounds.max;

    function update(fromUser) {
      var minVal = Math.min(parseFloat(minInput.value), parseFloat(maxInput.value));
      var maxVal = Math.max(parseFloat(minInput.value), parseFloat(maxInput.value));
      state.priceMin = minVal;
      state.priceMax = maxVal;

      var range = bounds.max - bounds.min || 1;
      var leftPct = ((minVal - bounds.min) / range) * 100;
      var rightPct = ((maxVal - bounds.min) / range) * 100;
      if (fill) {
        fill.style.left = leftPct + '%';
        fill.style.width = (rightPct - leftPct) + '%';
      }
      if (minLabel) minLabel.textContent = formatRupees(minVal);
      if (maxLabel) maxLabel.textContent = formatRupees(maxVal);

      if (fromUser) onChange();
    }

    minInput.addEventListener('input', function () { update(true); });
    maxInput.addEventListener('input', function () { update(true); });
    update(false);
  }

  function formatRupees(value) {
    return '₹' + Math.round(value).toLocaleString('en-IN');
  }

  /* ---------------- category bar ---------------- */

  function initCategoryBar(root, products, state, onChange) {
    var track = root.querySelector('[data-pe-cat-track]');
    var indicator = root.querySelector('[data-pe-cat-indicator]');
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-pe-cat-btn]'));

    function moveIndicator(btn) {
      if (!indicator || !btn) return;
      indicator.style.width = btn.offsetWidth + 'px';
      indicator.style.transform = 'translateX(' + btn.offsetLeft + 'px)';
    }

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        buttons.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        state.category = btn.getAttribute('data-collection');
        state.page = 1;
        moveIndicator(btn);
        onChange();
      });
    });

    var activeBtn = track ? track.querySelector('.is-active') : null;
    requestAnimationFrame(function () { moveIndicator(activeBtn); });
    window.addEventListener('resize', function () {
      moveIndicator(track ? track.querySelector('.is-active') : null);
    });
  }

  /* ---------------- search ---------------- */

  function initSearch(root, state, onChange) {
    var input = root.querySelector('[data-pe-search]');
    if (!input) return;
    var timer;
    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        state.search = input.value.trim().toLowerCase();
        state.page = 1;
        onChange();
      }, 150);
    });
  }

  /* ---------------- sort ---------------- */

  function initSort(root, state, onChange) {
    var select = root.querySelector('[data-pe-sort]');
    if (!select) return;
    select.addEventListener('change', function () {
      state.sort = select.value;
      state.page = 1;
      onChange();
    });
  }

  /* ---------------- mobile drawer ---------------- */

  function initMobileDrawer(root) {
    var toggle = root.querySelector('[data-pe-filters-toggle]');
    var closeBtn = root.querySelector('[data-pe-filters-close]');
    var sidebar = root.querySelector('[data-pe-sidebar]');
    var backdrop = root.querySelector('[data-pe-sidebar-backdrop]');

    function open() {
      sidebar.classList.add('is-open');
      backdrop.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    }
    function close() {
      sidebar.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    if (toggle) toggle.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);
  }

  /* ---------------- clear all ---------------- */

  function initClearAll(root, state, onChange) {
    var btn = root.querySelector('[data-pe-clear-filters]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      state.sizes.clear();
      state.display.clear();
      state.os.clear();
      state.availability.clear();
      state.type.clear();
      root.querySelectorAll('.pe-chip.is-active').forEach(function (chip) {
        chip.classList.remove('is-active');
      });
      var minInput = root.querySelector('[data-pe-price-min-input]');
      var maxInput = root.querySelector('[data-pe-price-max-input]');
      if (minInput && maxInput) {
        minInput.value = minInput.min;
        maxInput.value = maxInput.max;
        minInput.dispatchEvent(new Event('input'));
      }
      state.page = 1;
      onChange();
    });
  }

  /* ---------------- filtering / sorting ---------------- */

  function filterProducts(products, state) {
    return products.filter(function (p) {
      if (state.category !== 'all' && !p.tabs[state.category]) return false;
      if (state.search && p.title.indexOf(state.search) === -1) return false;
      if (state.sizes.size && !hasIntersection(p.sizes, state.sizes)) return false;
      if (state.display.size && !hasIntersection(p.display, state.display)) return false;
      if (state.os.size && !hasIntersection(p.os, state.os)) return false;
      if (state.type.size && !state.type.has(p.type)) return false;
      if (state.availability.size) {
        var label = p.available ? 'In Stock' : 'Out of Stock';
        if (!state.availability.has(label)) return false;
      }
      if (state.priceMin !== null && p.price < state.priceMin) return false;
      if (state.priceMax !== null && p.price > state.priceMax) return false;
      return true;
    });
  }

  function hasIntersection(arr, set) {
    for (var i = 0; i < arr.length; i++) {
      if (set.has(arr[i])) return true;
    }
    return false;
  }

  function sortProducts(products, sort) {
    var copy = products.slice();
    switch (sort) {
      case 'price-asc':
        copy.sort(function (a, b) { return a.price - b.price; });
        break;
      case 'price-desc':
        copy.sort(function (a, b) { return b.price - a.price; });
        break;
      case 'alpha':
        copy.sort(function (a, b) { return a.title.localeCompare(b.title); });
        break;
      case 'best-selling':
        copy.sort(function (a, b) { return a.rank - b.rank; });
        break;
      case 'newest':
      default:
        copy.sort(function (a, b) { return b.created - a.created; });
        break;
    }
    return copy;
  }

  /* ---------------- rendering ---------------- */

  function renderGrid(root, grid, filtered, state, perPage) {
    var totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    if (state.page > totalPages) state.page = totalPages;
    var start = (state.page - 1) * perPage;
    var pageItems = filtered.slice(start, start + perPage);

    grid.innerHTML = '';
    pageItems.forEach(function (p) {
      grid.appendChild(p.el);
    });

    var emptyEl = root.querySelector('[data-pe-empty]');
    if (emptyEl) emptyEl.hidden = filtered.length !== 0;
    grid.hidden = filtered.length === 0;
  }

  function renderResultCount(root, count, total) {
    var el = root.querySelector('[data-pe-result-count]');
    if (!el) return;
    el.textContent = count + (count === 1 ? ' product' : ' products');
  }

  function renderPagination(root, filtered, state, perPage, onChange) {
    var nav = root.querySelector('[data-pe-pagination]');
    if (!nav) return;
    var totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    nav.innerHTML = '';

    if (totalPages <= 1) return;

    var prev = document.createElement('button');
    prev.type = 'button';
    prev.textContent = 'Prev';
    prev.disabled = state.page <= 1;
    prev.addEventListener('click', function () {
      state.page = Math.max(1, state.page - 1);
      onChange();
      scrollToGrid(root);
    });
    nav.appendChild(prev);

    for (var i = 1; i <= totalPages; i++) {
      (function (pageNum) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = pageNum;
        if (pageNum === state.page) btn.classList.add('is-active');
        btn.addEventListener('click', function () {
          state.page = pageNum;
          onChange();
          scrollToGrid(root);
        });
        nav.appendChild(btn);
      })(i);
    }

    var next = document.createElement('button');
    next.type = 'button';
    next.textContent = 'Next';
    next.disabled = state.page >= totalPages;
    next.addEventListener('click', function () {
      state.page = Math.min(totalPages, state.page + 1);
      onChange();
      scrollToGrid(root);
    });
    nav.appendChild(next);
  }

  function scrollToGrid(root) {
    var wrap = root.querySelector('.pe-grid-wrap');
    if (!wrap) return;
    var top = wrap.getBoundingClientRect().top + window.pageYOffset - 100;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  function updateFilterCountBadge(root, state) {
    var badge = root.querySelector('[data-pe-filter-count]');
    if (!badge) return;
    var count = state.sizes.size + state.display.size + state.os.size + state.availability.size + state.type.size;
    if (count > 0) {
      badge.hidden = false;
      badge.textContent = count;
    } else {
      badge.hidden = true;
    }
  }
})();
